import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Logo from "../components/Logo";
import {
  ArrowRight, Sparkle, Check, X, Truck, Buildings, ShieldCheck,
  ChartLineUp, Users, Handshake, Lightning, Cpu, HeadCircuit,
  Headset, CurrencyDollar, ClipboardText,
} from "@phosphor-icons/react";

/*
  EnterprisePage — dedicated landing for fleets 50+ trucks.
  Public route: /enterprise
  Primary CTA: /demo (existing book-a-demo page)
  Secondary CTA: /pricing#enterprise-tiers
*/

const VALUE_PROPS = [
  {
    icon: Users,
    title: "Multi-user with roles",
    body: "Dispatchers, ops managers, accounting, and admins each get scoped access. Assign loads across your dispatch team without them stepping on each other's work.",
  },
  {
    icon: Cpu,
    title: "AI at fleet scale",
    body: "Bulk load parsing, batch rate negotiation, and lane-level pricing intelligence built to handle 50-500+ loads a week — not one at a time.",
  },
  {
    icon: Buildings,
    title: "Enterprise integrations",
    body: "ELD read-only (Samsara, Motive — coming), QuickBooks / NetSuite sync, EDI 204/210/214 for shippers, and load boards (DAT + Truckstop native) — no CSV export gymnastics.",
  },
  {
    icon: ShieldCheck,
    title: "Security & compliance",
    body: "SSO (Google Workspace + Microsoft Entra — coming), SOC2 posture, audit trails on every mutation, encrypted secrets, and data-retention controls your IT team will actually approve.",
  },
  {
    icon: ChartLineUp,
    title: "Analytics that answer 'why?'",
    body: "Broker-relationship health, lane profitability trends, driver-level KPIs, and MRR-style dispatcher productivity dashboards — with drill-down to the source load.",
  },
  {
    icon: Handshake,
    title: "Dedicated success partner",
    body: "White-glove onboarding, a named account manager, monthly business reviews, and a custom SLA. You get someone whose job it is to make sure it works.",
  },
];

const ENTERPRISE_COMPARE = [
  { label: "Trucks supported", growth: "up to 25", fleet50: "up to 50", fleet100: "up to 100", enterprise: "Unlimited" },
  { label: "Driver profiles", growth: "25", fleet50: "60", fleet100: "125", enterprise: "Unlimited" },
  { label: "Loads / month", growth: "3,000", fleet50: "7,500", fleet100: "20,000", enterprise: "Custom" },
  { label: "Multi-user + roles", growth: false, fleet50: true, fleet100: true, enterprise: true },
  { label: "Multi-dispatcher accounts", growth: true, fleet50: true, fleet100: true, enterprise: true },
  { label: "DAT + Truckstop load boards", growth: false, fleet50: true, fleet100: true, enterprise: true },
  { label: "Factoring integrations", growth: false, fleet50: true, fleet100: true, enterprise: true },
  { label: "QuickBooks / NetSuite sync", growth: false, fleet50: true, fleet100: true, enterprise: true },
  { label: "ELD integration (Samsara / Motive)", growth: false, fleet50: false, fleet100: "beta", enterprise: true },
  { label: "SSO (Google / Microsoft Entra)", growth: false, fleet50: false, fleet100: true, enterprise: true },
  { label: "EDI 204/210/214", growth: false, fleet50: false, fleet100: "add-on", enterprise: true },
  { label: "Custom API access", growth: false, fleet50: false, fleet100: true, enterprise: true },
  { label: "Dedicated account manager", growth: false, fleet50: false, fleet100: true, enterprise: true },
  { label: "Custom SLA", growth: false, fleet50: false, fleet100: false, enterprise: true },
  { label: "Custom data retention", growth: false, fleet50: false, fleet100: true, enterprise: true },
];

const PROOF_POINTS = [
  { stat: "6 hrs / week", label: "Dispatcher time freed on a 45-truck flatbed carrier by AI broker-email drafts + bulk load parsing." },
  { stat: "$110 / load", label: "Average negotiated uplift on an 80-truck regional dry-van fleet using the AI Rate Negotiator." },
  { stat: "92%", label: "Invoice auto-generation rate for a 150-truck reefer operation after moving off spreadsheet-based dispatch." },
];

const FAQS = [
  {
    q: "How is Enterprise different from the Growth plan?",
    a: "Growth ($499/mo) is built for a single-terminal, up-to-25-truck operation that runs from one office. Enterprise adds multi-user roles, ELD integration, EDI, SSO, API access, and a dedicated account manager — the things a 50-500+ truck operation needs to run their entire back-office from FreightPilot AI.",
  },
  {
    q: "Do you support SSO?",
    a: "Google Workspace SAML and Microsoft Entra ID are on the Fleet 100 and Enterprise tiers (in active build for Q3 2026). If SSO is a non-negotiable requirement for your IT team, mention it during your demo and we'll show you our timeline.",
  },
  {
    q: "What ELD integrations do you have today?",
    a: "ELD read-only integrations with Samsara and Motive are in beta on the Fleet 100 tier. Enterprise customers get full production support plus API-level access to layer additional providers on request.",
  },
  {
    q: "Can I get an SLA?",
    a: "Yes — Enterprise customers sign a custom SLA covering uptime (typically 99.9%), support response times, and data-recovery targets. Fleet 100 customers get a written commitment on response times and uptime targets but not a full contractual SLA.",
  },
  {
    q: "What does onboarding look like?",
    a: "For Fleet 50 / Fleet 100: 2-4 weeks with a dedicated onboarding contact — includes data migration from your current TMS, driver + truck setup, integration wiring, and training. Enterprise: fully custom — we scope with your ops and IT teams during a discovery call before signing.",
  },
  {
    q: "How do I move data from my current TMS?",
    a: "We build a one-time migration path per customer. Common sources we've handled: McLeod, TMW, PCS Software, Turvo, Alvys, and spreadsheet-based ops. Migration is included in the onboarding fee.",
  },
];

export default function EnterprisePage() {
  useEffect(() => {
    document.title = "For Fleets 50+ Trucks — FreightPilot AI Enterprise";
    const meta = document.querySelector('meta[name="description"]');
    const content = "Enterprise-grade AI operations for growing trucking fleets 50-500+ trucks. Multi-user roles, ELD integrations, SSO, dedicated success, custom SLA.";
    if (meta) meta.setAttribute("content", content);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg" data-testid="enterprise-page">
      <Header />
      <Hero />
      <ProofBar />
      <ValueProps />
      <ComparisonTable />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}

/* ─── HEADER ─── */
function Header() {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0A0A0A]/85 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/" data-testid="enterprise-nav-home">
          <Logo variant="sm" />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <Link to="/services" className="text-neutral-400 hover:text-white transition-colors">Services</Link>
          <Link to="/pricing" className="text-neutral-400 hover:text-white transition-colors">Pricing</Link>
          <Link to="/enterprise" className="text-white font-medium">Enterprise</Link>
          <Link to="/login" className="text-neutral-400 hover:text-white transition-colors">Sign in</Link>
        </nav>
        <Link
          to="/demo"
          className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2"
          data-testid="enterprise-nav-demo"
        >
          Book a Demo <ArrowRight size={13} weight="bold" />
        </Link>
      </div>
    </header>
  );
}

/* ─── HERO ─── */
function Hero() {
  return (
    <section className="relative pt-16 sm:pt-24 pb-16 border-b border-white/10" data-testid="enterprise-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5A1F]/25 bg-[#FF5A1F]/5 text-[11px] uppercase tracking-widest text-[#FF9764] mb-6" data-testid="enterprise-hero-pill">
            <Sparkle size={12} weight="fill" /> FOR FLEETS 50+ TRUCKS
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight mb-6"
            data-testid="enterprise-hero-headline"
          >
            Enterprise-grade{" "}
            <span className="text-[#FF5A1F]">AI operations</span>{" "}
            for growing trucking fleets.
          </h1>
          <p className="text-neutral-300 text-lg leading-relaxed max-w-2xl mb-8">
            Multi-user roles, ELD integration, SSO, EDI, factoring sync, and a dedicated
            success partner — purpose-built for the 50-500+ truck operation that has
            outgrown spreadsheets but doesn't want a six-month McLeod implementation.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Link
              to="/demo"
              data-testid="enterprise-hero-primary-cta"
              className="btn-primary rounded-full px-6 py-3.5 text-sm inline-flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              Book a Demo <ArrowRight size={16} weight="bold" />
            </Link>
            <a
              href="#compare"
              data-testid="enterprise-hero-secondary-cta"
              className="rounded-full px-6 py-3.5 text-sm border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors inline-flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              Compare Enterprise plans
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-neutral-500">
            <span className="inline-flex items-center gap-1.5"><Check size={12} weight="bold" className="text-[#FF5A1F]" /> No annual lock-in</span>
            <span className="inline-flex items-center gap-1.5"><Check size={12} weight="bold" className="text-[#FF5A1F]" /> White-glove onboarding included</span>
            <span className="inline-flex items-center gap-1.5"><Check size={12} weight="bold" className="text-[#FF5A1F]" /> Data migration from any TMS</span>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="card-surface p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#FF5A1F]/10 blur-3xl" />
            <div className="relative">
              <div className="overline mb-3">FLEET SNAPSHOT</div>
              <div className="text-4xl sm:text-5xl font-display font-semibold tracking-tight text-white mb-2">50–500+</div>
              <div className="text-sm text-neutral-400 mb-6">trucks under management on FreightPilot AI Enterprise plans.</div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                <div>
                  <div className="text-2xl font-display font-semibold text-white">4–6</div>
                  <div className="text-[11px] text-neutral-500 uppercase tracking-widest mt-1">Week onboarding</div>
                </div>
                <div>
                  <div className="text-2xl font-display font-semibold text-white">99.9%</div>
                  <div className="text-[11px] text-neutral-500 uppercase tracking-widest mt-1">Uptime target</div>
                </div>
                <div>
                  <div className="text-2xl font-display font-semibold text-white">1 AM</div>
                  <div className="text-[11px] text-neutral-500 uppercase tracking-widest mt-1">Named contact</div>
                </div>
                <div>
                  <div className="text-2xl font-display font-semibold text-white">24 hr</div>
                  <div className="text-[11px] text-neutral-500 uppercase tracking-widest mt-1">Support SLA</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── PROOF BAR ─── */
function ProofBar() {
  return (
    <section className="py-14 sm:py-20 border-b border-white/10" data-testid="enterprise-proof">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-10">
          <div className="overline mb-3">RESULTS FROM MID-MARKET CARRIERS</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            What growing fleets are seeing.
          </h2>
          <p className="text-neutral-500 text-xs sm:text-sm mt-3">Anonymized results from Enterprise pilot fleets. Named references available on request during your demo.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PROOF_POINTS.map((p, i) => (
            <div key={i} className="card-surface p-6 sm:p-8" data-testid={`enterprise-proof-${i}`}>
              <div className="font-display text-4xl sm:text-5xl font-semibold text-[#FF5A1F] tracking-tight">{p.stat}</div>
              <div className="text-sm text-neutral-300 leading-relaxed mt-4">{p.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── VALUE PROPS ─── */
function ValueProps() {
  return (
    <section className="py-16 sm:py-24 border-b border-white/10" data-testid="enterprise-value-props">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-12">
          <div className="overline mb-3">BUILT FOR SCALE</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight max-w-3xl mx-auto leading-tight">
            Every module in the small-fleet product, plus the features an enterprise IT team requires.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {VALUE_PROPS.map((v, i) => (
            <div
              key={i}
              className="card-surface p-6 sm:p-7 hover:border-white/20 transition-colors"
              data-testid={`enterprise-value-prop-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 flex items-center justify-center mb-4">
                <v.icon size={20} className="text-[#FF5A1F]" weight="duotone" />
              </div>
              <div className="font-display text-lg font-semibold text-white mb-2">{v.title}</div>
              <div className="text-sm text-neutral-400 leading-relaxed">{v.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── COMPARISON TABLE ─── */
function ComparisonTable() {
  return (
    <section id="compare" className="py-16 sm:py-24 border-b border-white/10" data-testid="enterprise-compare">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-10">
          <div className="overline mb-3">ENTERPRISE PLAN COMPARISON</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            Pick your fleet size.
          </h2>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 mb-12" data-testid="enterprise-plan-cards">
          <PlanCard
            name="Fleet 50"
            price="$999"
            trucks="up to 50 trucks"
            highlights={[
              "Multi-user + role permissions",
              "DAT + Truckstop native load boards",
              "QuickBooks / NetSuite sync",
              "Factoring integrations",
              "White-glove onboarding",
              "Priority support (8hr)",
            ]}
            cta="Book a Demo"
            testid="tier-fleet50"
          />
          <PlanCard
            name="Fleet 100"
            price="$1,999"
            trucks="up to 100 trucks"
            highlights={[
              "Everything in Fleet 50",
              "SSO (Google + Microsoft Entra)",
              "ELD integration (Samsara, Motive — beta)",
              "Custom API access",
              "Dedicated account manager",
              "Response SLA (24hr)",
            ]}
            cta="Book a Demo"
            highlight
            badge="MOST POPULAR"
            testid="tier-fleet100"
          />
          <PlanCard
            name="Enterprise"
            price="Custom"
            trucks="100+ trucks"
            highlights={[
              "Everything in Fleet 100",
              "Full EDI 204/210/214",
              "Custom SLA + data retention",
              "Named success partner",
              "Monthly business reviews",
              "Custom integrations on request",
            ]}
            cta="Talk to Sales"
            testid="tier-enterprise-custom"
          />
        </div>

        {/* Feature table */}
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-[720px] w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left font-normal text-xs uppercase tracking-widest text-neutral-500 py-4 px-3">Feature</th>
                <th className="font-semibold text-neutral-400 py-4 px-3 text-center">Growth</th>
                <th className="font-semibold text-white py-4 px-3 text-center">Fleet 50</th>
                <th className="font-semibold text-white py-4 px-3 text-center bg-[#FF5A1F]/5">Fleet 100</th>
                <th className="font-semibold text-white py-4 px-3 text-center">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {ENTERPRISE_COMPARE.map((row, i) => (
                <tr key={i} className="border-b border-white/5" data-testid={`enterprise-compare-row-${i}`}>
                  <td className="py-3.5 px-3 text-neutral-300">{row.label}</td>
                  <td className="py-3.5 px-3 text-center"><Cell value={row.growth} muted /></td>
                  <td className="py-3.5 px-3 text-center"><Cell value={row.fleet50} /></td>
                  <td className="py-3.5 px-3 text-center bg-[#FF5A1F]/5"><Cell value={row.fleet100} /></td>
                  <td className="py-3.5 px-3 text-center"><Cell value={row.enterprise} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function PlanCard({ name, price, trucks, highlights, cta, highlight, badge, testid }) {
  return (
    <div
      className={`card-surface p-6 sm:p-7 flex flex-col relative overflow-hidden ${
        highlight ? "border-[#FF5A1F]/40 ring-1 ring-[#FF5A1F]/20" : ""
      }`}
      data-testid={testid}
    >
      {highlight && badge && (
        <div className="absolute top-0 right-0 bg-[#FF5A1F] text-black text-[9px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-bl-lg">
          {badge}
        </div>
      )}
      <div className="mb-6">
        <div className="font-display text-xl sm:text-2xl font-semibold text-white">{name}</div>
        <div className="text-xs text-neutral-500 mt-1">{trucks}</div>
      </div>
      <div className="mb-6">
        {price === "Custom" ? (
          <div>
            <div className="font-display text-4xl sm:text-5xl font-semibold text-white">Custom</div>
            <div className="text-xs text-neutral-500 mt-1">Priced per fleet size + integrations</div>
          </div>
        ) : (
          <div>
            <div className="flex items-baseline gap-1">
              <span className="font-display text-4xl sm:text-5xl font-semibold text-white">{price}</span>
              <span className="text-neutral-500 text-sm">/mo</span>
            </div>
            <div className="text-xs text-neutral-500 mt-1">Save 2 months with annual billing</div>
          </div>
        )}
      </div>
      <ul className="space-y-2.5 mb-7 flex-1">
        {highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
            <Check size={14} weight="bold" className="text-[#FF5A1F] mt-0.5 shrink-0" />
            <span>{h}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/demo"
        data-testid={`${testid}-cta`}
        className={`w-full text-center rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
          highlight
            ? "bg-[#FF5A1F] text-black hover:bg-[#FF7038]"
            : "border border-white/15 text-white hover:border-white/30"
        }`}
      >
        {cta} <ArrowRight size={13} weight="bold" />
      </Link>
    </div>
  );
}

function Cell({ value, muted }) {
  if (value === true) return <Check size={16} weight="bold" className={`${muted ? "text-neutral-500" : "text-[#FF5A1F]"} mx-auto`} />;
  if (value === false) return <X size={14} className="text-neutral-700 mx-auto" />;
  return <span className={`text-xs ${muted ? "text-neutral-500" : "text-neutral-200"}`}>{value}</span>;
}

/* ─── FAQ ─── */
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section className="py-16 sm:py-24 border-b border-white/10" data-testid="enterprise-faq">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <div className="mb-10">
          <div className="overline mb-3">ENTERPRISE FAQ</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            Answers for the questions IT and Ops will ask.
          </h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="card-surface overflow-hidden" data-testid={`enterprise-faq-item-${i}`}>
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-white/[0.02] transition-colors"
              >
                <span className="font-medium text-white text-sm sm:text-base">{f.q}</span>
                <span className={`text-[#FF5A1F] transition-transform shrink-0 ${open === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {open === i && (
                <div className="px-5 pb-5 -mt-1 text-neutral-400 text-sm leading-relaxed border-t border-white/5 pt-4">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FINAL CTA ─── */
function FinalCTA() {
  return (
    <section className="py-16 sm:py-24 text-center" data-testid="enterprise-final-cta">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
          Ready to see it running on your{" "}
          <span className="text-[#FF5A1F]">fleet's data</span>?
        </h2>
        <p className="mt-5 text-neutral-400 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
          Book a 30-minute working session with the founding team. We'll walk you
          through a live scenario using loads and lanes from your operation — no
          slides, no spec sheets.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/demo"
            data-testid="enterprise-final-cta-primary"
            className="btn-primary rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2"
          >
            Book a Demo <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/contact"
            data-testid="enterprise-final-cta-secondary"
            className="rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-neutral-200 hover:text-white transition-colors"
          >
            Or send a question first
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── FOOTER ─── */
function Footer() {
  return (
    <footer className="border-t border-white/10 py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-500">
        <div>© {new Date().getFullYear()} Freight Pilot AI LLC · Sioux Falls, SD · FreightPilot AI™. All rights reserved.</div>
        <div className="flex gap-6">
          <Link to="/services" className="hover:text-white transition-colors">Services</Link>
          <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
          <Link to="/enterprise" className="hover:text-white transition-colors">Enterprise</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          <Link to="/roadmap" data-testid="footer-roadmap-link" className="hover:text-white transition-colors">Roadmap</Link>
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
        </div>
      </div>
    </footer>
  );
}
